import { presetDrift, settingLeaves } from '@ayumia/component-runtime';
import type { Template } from '../layout-model';
import type { FieldPresetBody } from '@ayumia/contracts';
import type { PresetRow } from './library';
import type { Entry } from './widgets';

/**
 * Préréglages dans l'éditeur v2 (ADR-146 addendum (14), (16) décision 4, planches « EditeurV2-Prereglage »,
 * « -Reutiliser », « -PrereglageImpacts ») : une entrée recopie son préréglage et le cite à sa version exacte ;
 * tout réglage qui en diffère est déclaré « modifié ici » (`preset.overrides`), sinon la publication le refuse
 * (`PRESET_DRIFT`). « Revenir » remet la valeur du préréglage ; « Se détacher » retire la citation.
 */

type Json = Record<string, unknown>;

export interface PresetUse {
  ref: string;
  id: string;
  version: string;
  overrides: string[];
}

export function presetOf(entry: Entry): PresetUse | undefined {
  const preset = (entry as unknown as { preset?: { ref: string; overrides?: string[] } }).preset;
  if (preset === undefined) return undefined;
  const at = preset.ref.lastIndexOf('@');
  return { ref: preset.ref, id: preset.ref.slice(0, at), version: preset.ref.slice(at + 1), overrides: preset.overrides ?? [] };
}

/** Préréglage cité, trouvé dans la bibliothèque à sa version exacte (sinon une autre version du même). */
export function presetRowOf(entry: Entry, presets: readonly PresetRow[]): PresetRow | undefined {
  const use = presetOf(entry);
  if (use === undefined) return undefined;
  return presets.find((row) => row.artifact_id === use.id && row.artifact_version === use.version);
}

/**
 * Après un changement d'une entrée liée : les réglages qui diffèrent du préréglage deviennent « modifiés ici »,
 * ceux qui lui sont revenus égaux ne le sont plus. Préréglage introuvable : la liste ne bouge pas.
 */
export function trackOverrides(entry: Entry, preset: FieldPresetBody | undefined): Entry {
  const use = presetOf(entry);
  if (use === undefined || preset === undefined) return entry;
  const drift = presetDrift(preset, entry as unknown as Json, []).filter((pointer) => /^\/(field|presentation|target|evidence|warnings|duplicates|required_when|readonly_when|visible_when)(\/|$)/.test(pointer));
  // Une surcharge garde la granularité la plus fine (une langue d'un libellé, une borne de cible).
  const overrides = [...new Set(drift)].sort().slice(0, 50);
  const next = { ...(entry as unknown as Json) };
  next['preset'] = overrides.length === 0 ? { ref: use.ref } : { ref: use.ref, overrides };
  return next as unknown as Entry;
}

const unescape = (segment: string): string => segment.replace(/~1/g, '/').replace(/~0/g, '~');

function valueAt(root: unknown, pointer: string): unknown {
  let node = root;
  for (const segment of pointer.split('/').slice(1).map(unescape)) {
    if (node === null || typeof node !== 'object') return undefined;
    node = (node as Json)[segment];
  }
  return node;
}

function writeAt(root: Json, pointer: string, value: unknown): Json {
  const segments = pointer.split('/').slice(1).map(unescape);
  const write = (node: unknown, index: number): unknown => {
    const base = node !== null && typeof node === 'object' && !Array.isArray(node) ? { ...(node as Json) } : {};
    const key = segments[index]!;
    if (index === segments.length - 1) {
      if (value === undefined) delete base[key];
      else base[key] = structuredClone(value);
    } else {
      const child = write(base[key], index + 1) as Json;
      if (Object.keys(child).length === 0) delete base[key];
      else base[key] = child;
    }
    return base;
  };
  return write(root, 0) as Json;
}

/** Valeur d'un réglage dans le préréglage, à un pointeur de l'entrée (`/field/…`, `/presentation/…`). */
export function presetValueAt(preset: FieldPresetBody, pointer: string): unknown {
  if (pointer.startsWith('/field/')) return valueAt(preset.field, pointer.slice(6));
  if (pointer === '/presentation/widget') return valueAt(preset.entry ?? {}, pointer) ?? preset.widget;
  return valueAt(preset.entry ?? {}, pointer);
}

/** « Revenir au préréglage » pour un réglage modifié ici. */
export function revertOverride(entry: Entry, preset: FieldPresetBody, pointer: string): Entry {
  const reverted = writeAt(entry as unknown as Json, pointer, presetValueAt(preset, pointer));
  return trackOverrides(reverted as unknown as Entry, preset);
}

/** « Se détacher » : l'entrée garde ses réglages et ne suit plus le préréglage. */
export function detachPreset(entry: Entry): Entry {
  const next = { ...(entry as unknown as Json) };
  delete next['preset'];
  return next as unknown as Entry;
}

const ENTRY_KEYS = ['presentation', 'target', 'evidence', 'warnings', 'duplicates', 'required_when', 'readonly_when', 'visible_when'] as const;

/** Corps d'un préréglage tiré d'une entrée (« Enregistrer comme préréglage », « Mettre à jour le préréglage »). */
export function presetBodyFrom(entry: Entry, options: { label: Record<string, string>; scope: 'all_forms' | 'listed_forms'; forms?: readonly string[]; base?: FieldPresetBody }): FieldPresetBody {
  const source = entry as unknown as Json;
  const presentation = { ...((source['presentation'] ?? {}) as Json) };
  const widget = (presentation['widget'] as string | undefined) ?? options.base?.widget ?? 'keypad';
  const kept: Json = {};
  for (const key of ENTRY_KEYS) if (source[key] !== undefined) kept[key] = structuredClone(source[key]);
  const field = structuredClone(source['field']) as Json;
  const optionSource = presentation['option_source'] as { object_type: string; field_id: string } | undefined;
  const binding = options.base?.binding ?? (optionSource === undefined ? undefined : { object_type: optionSource.object_type, field_id: optionSource.field_id });
  return {
    ...(options.base ?? {}),
    label: options.label,
    preset_kind: 'field',
    widget: widget as FieldPresetBody['widget'],
    field: field as unknown as FieldPresetBody['field'],
    ...(Object.keys(kept).length === 0 ? {} : { entry: kept as FieldPresetBody['entry'] }),
    ...(binding === undefined ? {} : { binding }),
    availability: options.scope === 'all_forms' ? { scope: 'all_forms' } : { scope: 'listed_forms', forms: [...(options.forms ?? [])] },
    propagation: 'automatic',
  } as FieldPresetBody;
}

/** Libellés en mots des réglages, pour « modifié ici » et les impacts (jamais un pointeur à l'écran). */
const SETTING_LABELS: ReadonlyArray<[RegExp, string]> = [
  [/^\/field\/label/, 'Libellé'],
  [/^\/field\/description/, 'Texte d’aide'],
  [/^\/field\/required/, 'Obligatoire'],
  [/^\/field\/unit/, 'Unité'],
  [/^\/field\/validation\/(min|max)$/, 'Bornes'],
  [/^\/field\/validation\/(min_length|max_length|pattern)/, 'Format'],
  [/^\/field\/validation\/enum/, 'Choix'],
  [/^\/field\/reference_type/, 'Objet à choisir'],
  [/^\/field\/type/, 'Type de donnée'],
  [/^\/presentation\/widget/, 'Widget'],
  [/^\/presentation\/scan\/sources/, 'Lire avec'],
  [/^\/presentation\/scan\/manual_entry/, 'Saisie à la main'],
  [/^\/presentation\/scan\/keyboard/, 'Clavier'],
  [/^\/presentation\/scan/, 'Lecture'],
  [/^\/presentation\/tiles\/per_row/, 'Tuiles par ligne'],
  [/^\/presentation\/tiles\/height/, 'Hauteur des tuiles'],
  [/^\/presentation\/tiles/, 'Disposition'],
  [/^\/presentation\/options/, 'Choix montrés'],
  [/^\/presentation\/option_visual/, 'Visuel des choix'],
  [/^\/presentation\/layout/, 'Largeur'],
  [/^\/presentation\/appearance/, 'Apparence avancée'],
  [/^\/presentation\/(size|empty_display|placeholder|decimals|step|variant)/, 'Apparence'],
  [/^\/presentation/, 'Présentation'],
  [/^\/target/, 'Cible'],
  [/^\/evidence/, 'Preuve'],
  [/^\/(required_when|visible_when|readonly_when)/, 'Contrôles'],
  [/^\/(warnings|duplicates)/, 'Contrôles'],
];

export const settingLabel = (pointer: string): string => SETTING_LABELS.find(([pattern]) => pattern.test(pointer))?.[1] ?? 'Réglage';

/** Réglages modifiés ici, regroupés par libellé (« Lire avec », « Tuiles par ligne »). */
export function overrideGroups(overrides: readonly string[]): Array<{ label: string; pointers: string[] }> {
  const groups: Array<{ label: string; pointers: string[] }> = [];
  for (const pointer of overrides) {
    const label = settingLabel(pointer);
    const found = groups.find((group) => group.label === label);
    if (found === undefined) groups.push({ label, pointers: [pointer] });
    else found.pointers.push(pointer);
  }
  return groups;
}

/**
 * Surcharges déclarées après un geste du canevas (renommer sur place, redimensionner, glisser) : chaque réglage
 * changé d'une entrée qui suit un préréglage devient « modifié ici », comme dans l'inspecteur (sinon la
 * publication refuserait l'écart, `PRESET_DRIFT`).
 */
export function markCanvasOverrides(before: Template, after: Template): Template {
  let changed = false;
  const sections = after.sections.map((section) => {
    const fields = section.fields.map((entry) => {
      const use = presetOf(entry as Entry);
      if (use === undefined) return entry;
      const previous = before.sections.find((candidate) => candidate.section_id === section.section_id)?.fields.find((candidate) => candidate.field.field_id === entry.field.field_id);
      if (previous === undefined || previous === entry) return entry;
      const was = settingLeaves(stripPreset(previous), '');
      const now = settingLeaves(stripPreset(entry), '');
      const pointers = [...new Set([...was.keys(), ...now.keys()])].filter((pointer) => was.get(pointer) !== now.get(pointer) && /^\/(field|presentation|target|evidence|warnings|duplicates|required_when|readonly_when|visible_when)\//.test(pointer) && !pointer.startsWith('/field/field_id'));
      const added = pointers.filter((pointer) => !use.overrides.some((override) => pointer === override || pointer.startsWith(`${override}/`)));
      if (added.length === 0) return entry;
      changed = true;
      return { ...entry, preset: { ref: use.ref, overrides: [...use.overrides, ...added].sort().slice(0, 50) } } as typeof entry;
    });
    return fields.every((entry, index) => entry === section.fields[index]) ? section : ({ ...section, fields } as typeof section);
  });
  return changed ? ({ ...after, sections } as Template) : after;
}

function stripPreset(entry: unknown): Json {
  const copy = { ...(entry as Json) };
  delete copy['preset'];
  return copy;
}
